import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { cn } from '@/utils/cn';
import type { AdsVendorOption } from '@/api/client';

export function vendorOptionLabel(vendor: AdsVendorOption): string {
  return (
    vendor.displayName?.trim() ||
    vendor.sellingPartnerId?.trim() ||
    vendor.credentialId
  );
}

function retailKindLabel(vendor: AdsVendorOption): string {
  return vendor.connectionType === 'seller' ? 'Seller Central' : 'Vendor Central';
}

export function AdsVendorLinkPicker({
  profileId,
  adsMarketplaceId,
  linkedVendors,
  availableVendors,
  saving = false,
  disabled = false,
  onSave,
}: {
  profileId: string;
  adsMarketplaceId?: string | null;
  linkedVendors: AdsVendorOption[];
  availableVendors: AdsVendorOption[];
  saving?: boolean;
  disabled?: boolean;
  onSave: (profileId: string, credentialIds: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const linkedIds = useMemo(
    () => new Set(linkedVendors.map((vendor) => vendor.credentialId)),
    [linkedVendors]
  );
  const options = useMemo(() => {
    const byId = new Map<string, AdsVendorOption>();
    for (const vendor of availableVendors) byId.set(vendor.credentialId, vendor);
    for (const vendor of linkedVendors) {
      if (!byId.has(vendor.credentialId)) byId.set(vendor.credentialId, vendor);
    }
    return [...byId.values()];
  }, [availableVendors, linkedVendors]);

  const mismatch = linkedVendors.some(
    (vendor) =>
      Boolean(adsMarketplaceId) &&
      Boolean(vendor.marketplaceId) &&
      vendor.marketplaceId !== adsMarketplaceId
  );
  const triggerLabel =
    linkedVendors.length === 0
      ? 'No retail account linked'
      : linkedVendors.length === 1
        ? vendorOptionLabel(linkedVendors[0])
        : `${linkedVendors.length} retail accounts`;

  const toggle = (credentialId: string, checked: boolean) => {
    const next = new Set(linkedIds);
    if (checked) next.add(credentialId);
    else next.delete(credentialId);
    onSave(profileId, [...next]);
  };

  return (
    <div className="min-w-0">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={disabled || saving}
            className="max-w-[16rem] justify-between gap-2"
            aria-label={`Retail account linked to this Ads account: ${triggerLabel}`}
          >
            <span className="truncate">{saving ? 'Saving…' : triggerLabel}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[min(22rem,calc(100vw-2rem))] p-2">
          {options.length === 0 ? (
            <div className="space-y-2 px-2 py-3 text-sm text-foreground-muted">
              <p>No Seller or Vendor Central connections yet.</p>
              <div className="flex flex-col gap-1">
                <Link
                  to="/connections?tab=seller"
                  className="font-medium text-brand hover:underline"
                  onClick={() => setOpen(false)}
                >
                  Connect Seller Central
                </Link>
                <Link
                  to="/connections?tab=vendor"
                  className="font-medium text-brand hover:underline"
                  onClick={() => setOpen(false)}
                >
                  Connect Vendor Central
                </Link>
              </div>
            </div>
          ) : (
            <ul className="max-h-64 space-y-0.5 overflow-y-auto">
              {options.map((vendor) => {
                const checked = linkedIds.has(vendor.credentialId);
                const warn =
                  checked &&
                  Boolean(adsMarketplaceId) &&
                  Boolean(vendor.marketplaceId) &&
                  vendor.marketplaceId !== adsMarketplaceId
                    ? 'Marketplace does not match this Ads account'
                    : null;
                return (
                  <li key={vendor.credentialId}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-page',
                        checked && 'bg-brand/5'
                      )}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={checked}
                        disabled={saving}
                        onChange={(event) => toggle(vendor.credentialId, event.target.checked)}
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-foreground">
                          {vendorOptionLabel(vendor)}
                        </span>
                        <span className="block truncate text-xs text-foreground-subtle">
                          {retailKindLabel(vendor)}
                          {vendor.marketplaceId ? ` · ${vendor.marketplaceId}` : ''}
                        </span>
                        {warn ? (
                          <span className="mt-0.5 block text-xs text-warning">{warn}</span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </PopoverContent>
      </Popover>
      {mismatch ? (
        <p className="mt-1 max-w-[16rem] text-xs text-warning">
          A linked account uses a different marketplace.
        </p>
      ) : null}
    </div>
  );
}
